import React, { useState } from 'react';

import { locale } from '@trqk/utils';

import { Button, CurrencyInput } from '../../..';

import { emptyEarningsPreFilterValue } from './constants';

/**
 * @typedef {EarningsPreFilterModalInput}
 * @property {EarningsPreFilterValue} filterValue
 * @property {function} setFilter a function that sets the filter value
 *
 */
interface EarningsPreFilterContentProps {
    /**
     * Closes the tooltip
     */
    closeTooltip(): void;
    /**
     * Deletes the filter values from the preFilter machine and from the URL parameters
     */
    deleteFilter(): void;
    /**
     * The current earnings filter value, which corresponds to two values within the preFilter
     */
    filterValue?: EarningsPreFilterValue;
    /**
     * Sets the filter values in the preFilter machine and in the URL parameters
     * @param value - see {@link EarningsPreFilterValue}
     */
    setFilter(value: EarningsPreFilterValue): void;
}

/**
 * A modal that allows the user to filter performances by a range of earnings.
 * @param {ModalContentProps<EarningsPreFilterModalInput>} props
 */
export const EarningsPreFilterContent = ({
    closeTooltip,
    deleteFilter,
    filterValue,
    setFilter,
}: EarningsPreFilterContentProps): JSX.Element => {
    const initialMin =
        filterValue === emptyEarningsPreFilterValue
            ? undefined
            : filterValue?.[0];
    const initialMax =
        filterValue === emptyEarningsPreFilterValue
            ? undefined
            : filterValue?.[1];
    const [min, setMin] = useState<number | undefined>(initialMin);
    const [max, setMax] = useState<number | undefined>(initialMax);

    return (
        <div className="p-12" style={{ width: 500 }}>
            <h5>
                {
                    locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.EARNINGS.MODAL
                        .TITLE
                }
            </h5>
            <button
                className="text-primary-500 mt-6 mb-2"
                type="button"
                onClick={(): void => {
                    deleteFilter();
                    closeTooltip();
                }}
            >
                {locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.EARNINGS.ALL}
            </button>
            <div className="mb-8 flex justify-between items-center">
                <div>
                    <div className="text-sm">
                        {
                            locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.EARNINGS
                                .MIN
                        }
                    </div>
                    <CurrencyInput
                        addClasses="border border-tertiary-300"
                        decimalPlaces={6}
                        onChange={(value: number): void => {
                            setMin(value);
                        }}
                        value={min}
                    />
                </div>
                <div className="mx-2 mt-4">to</div>
                <div>
                    <div className="text-sm">
                        {
                            locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.EARNINGS
                                .MAX
                        }
                    </div>
                    <CurrencyInput
                        addClasses="border border-tertiary-300"
                        decimalPlaces={6}
                        onChange={(value: number): void => {
                            setMax(value);
                        }}
                        min={min}
                        value={max}
                    />
                </div>
            </div>
            <div className="flex">
                <Button
                    addClasses="mr-2 text-center"
                    color="tertiary-light-bg"
                    handleClick={(): void => {
                        closeTooltip();
                    }}
                >
                    {
                        locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.EARNINGS
                            .CANCEL
                    }
                </Button>
                <Button
                    addClasses="ml-2 text-center"
                    color="primary-light-bg"
                    disabled={
                        typeof min !== 'number' || typeof max !== 'number'
                    }
                    handleClick={(): void => {
                        if (
                            setFilter !== null &&
                            typeof min === 'number' &&
                            typeof max === 'number'
                        ) {
                            setFilter([min, max]);
                        }

                        closeTooltip();
                    }}
                >
                    {locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.EARNINGS.APPLY}
                </Button>
            </div>
        </div>
    );
};
